import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, CheckCircle, Trash2, Mail, Calendar, Shield, Key } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/client/lib/utils';
import { apiFetch } from '@/client/lib/api';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/client/components/ui/card';
import { Badge } from '@/client/components/ui/badge';
import { Skeleton } from '@/client/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/client/components/ui/avatar';

export function AdminUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', userId],
    queryFn: async () => {
      const response = await apiFetch(`/api/admin/users/${userId}`);
      if (!response.ok) throw new Error('User not found');
      return response.json();
    },
  });

  const banMutation = useMutation({
    mutationFn: async () => {
      const response = await apiFetch(`/api/admin/users/${userId}/ban`, { method: 'POST' });
      if (!response.ok) throw new Error('Failed to ban user');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('User banned');
    },
  });

  const unbanMutation = useMutation({
    mutationFn: async () => {
      const response = await apiFetch(`/api/admin/users/${userId}/unban`, { method: 'POST' });
      if (!response.ok) throw new Error('Failed to unban user');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('User unbanned');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await apiFetch(`/api/admin/users/${userId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete user');
    },
    onSuccess: () => {
      toast.success('User deleted');
      navigate('/admin/users');
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="size-10 rounded-xl" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-64 rounded-2xl" />
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      </div>
    );
  }

  const user = data?.user;
  if (!user) {
    return (
      <Card className="rounded-2xl py-0">
        <CardContent className="p-12 text-center">
          <p className="text-muted-foreground">User not found</p>
          <Button asChild variant="outline" className="mt-4 rounded-xl">
            <Link to="/admin/users">Back to Users</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 stagger-children">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" asChild className="rounded-xl">
          <Link to="/admin/users">
            <ArrowLeft className="size-5" />
          </Link>
        </Button>
        <div className="flex items-center gap-4 flex-1">
          <Avatar className="size-16">
            <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-bold">
              {user.name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{user.name}</h1>
              {user.role === 'admin' && <Badge variant="primary-subtle">Admin</Badge>}
              {user.banned && <Badge variant="destructive">Banned</Badge>}
            </div>
            <p className="text-muted-foreground flex items-center gap-1.5 mt-1">
              <Mail className="size-4" />
              {user.email}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {user.banned ? (
            <Button
              variant="outline"
              onClick={() => unbanMutation.mutate()}
              disabled={unbanMutation.isPending}
              className="rounded-xl"
            >
              <CheckCircle className="size-4" />
              Unban
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => banMutation.mutate()}
              disabled={banMutation.isPending}
              className="rounded-xl text-destructive hover:bg-destructive/10"
            >
              <Ban className="size-4" />
              Ban
            </Button>
          )}
          <Button
            variant="destructive"
            onClick={() => {
              if (
                confirm('Are you sure you want to delete this user? This action cannot be undone.')
              ) {
                deleteMutation.mutate();
              }
            }}
            disabled={deleteMutation.isPending}
            className="rounded-xl"
          >
            <Trash2 className="size-4" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* User Information */}
        <Card className="rounded-2xl py-0">
          <CardHeader className="pb-0">
            <CardTitle className="flex items-center gap-2">
              <Shield className="size-5 text-muted-foreground" />
              User Information
            </CardTitle>
          </CardHeader>
          <CardContent className="py-6 space-y-4">
            <div className="flex justify-between items-center p-3 rounded-xl bg-muted/50">
              <span className="text-sm text-muted-foreground">Role</span>
              <Badge
                variant={user.role === 'admin' ? 'primary-subtle' : 'secondary'}
                className="capitalize"
              >
                {user.role}
              </Badge>
            </div>
            <div className="flex justify-between items-center p-3 rounded-xl bg-muted/50">
              <span className="text-sm text-muted-foreground">Status</span>
              <Badge variant={user.banned ? 'destructive' : 'success'}>
                {user.banned ? 'Banned' : 'Active'}
              </Badge>
            </div>
            <div className="flex justify-between items-center p-3 rounded-xl bg-muted/50">
              <span className="text-sm text-muted-foreground">Email Verified</span>
              <Badge variant={user.emailVerified ? 'success' : 'secondary'}>
                {user.emailVerified ? 'Verified' : 'Not Verified'}
              </Badge>
            </div>
            <div className="flex justify-between items-center p-3 rounded-xl bg-muted/50">
              <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                <Calendar className="size-3.5" />
                Created
              </span>
              <span className="font-medium">{formatDate(user.createdAt)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Usage */}
        <Card className="rounded-2xl py-0">
          <CardHeader className="pb-0">
            <CardTitle className="flex items-center gap-2">
              <Key className="size-5 text-muted-foreground" />
              Usage
            </CardTitle>
          </CardHeader>
          <CardContent className="py-6 space-y-4">
            <div className="flex justify-between items-center p-3 rounded-xl bg-muted/50">
              <span className="text-sm text-muted-foreground">Legacy API Key</span>
              <Badge variant={user.hasShlinkKey ? 'success' : 'secondary'}>
                {user.hasShlinkKey ? 'Configured' : 'Not configured'}
              </Badge>
            </div>
            <div className="flex justify-between items-center p-3 rounded-xl bg-muted/50">
              <span className="text-sm text-muted-foreground">API Tokens</span>
              <span className="font-medium">{user.apiTokenCount}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
